import { ThemeColorMode, UserRoles } from 'src/common/enums';
import type { ThmRouteObject } from 'src/common/interfaces';
import { routeProtector } from 'src/router/route-protector';
import { fullScreen, rootHandle, withoutFooter } from '../helpers';
export const ttxRoutes: ThmRouteObject[] = [{
  path: '/tabletop-exercises',
  loader: routeProtector().requireFeatureFlag('ttx'),
  async lazy() {
    const {
      Ttx
    } = await import('src/features/ttx');
    return {
      Component: Ttx
    };
  },
  handle: rootHandle,
  children: [{
    path: '',
    async lazy() {
      const {
        TTXHome
      } = await import('src/features/ttx/landing-sections/ttx-home');
      return {
        Component: TTXHome
      };
    },
    handle: {
      ...rootHandle,
      name: 'ttx_home',
      isHeaderTransparent: true
    }
  }, {
    path: 'evaluation/public/:encryptedRunId',
    async lazy() {
      const {
        Evaluation
      } = await import('src/features/ttx/evaluation');
      return {
        Component: props => <Evaluation {...props} isPublic />
      };
    },
    handle: {
      hideHeader: true,
      ...withoutFooter,
      name: 'evaluation_summary_public'
    }
  }, {
    path: '',
    loader: routeProtector().requireCompletedOnboardingAndWelcome(),
    handle: withoutFooter,
    children: [{
      // guard wrapper for routes that should not be accessible if there is an active run
      path: '',
      async lazy() {
        const {
          TtxActiveRunGuard
        } = await import('src/features/ttx');
        return {
          Component: TtxActiveRunGuard
        };
      },
      children: [{
        path: 'exercises',
        async lazy() {
          const {
            TTXDashboard
          } = await import('src/features/ttx/landing-sections/ttx-dashboard');
          return {
            Component: TTXDashboard
          };
        },
        handle: {
          ...withoutFooter,
          name: 'ttx_dashboard',
          isHeaderTransparent: true
        }
      }, {
        path: 'reports',
        loader: routeProtector().requireFeatureFlag('ttx-marketing-page', '/tabletop-exercises/exercises'),
        async lazy() {
          const {
            TTXReports
          } = await import('src/features/ttx/landing-sections/ttx-reports');
          return {
            Component: TTXReports
          };
        },
        handle: {
          ...withoutFooter,
          name: 'ttx_reports',
          isHeaderTransparent: true
        }
      }, {
        path: 'profiles',
        loader: routeProtector().requireFeatureFlag('ttx-marketing-page', '/ttx/dashboard'),
        async lazy() {
          const {
            TTXCompanyProfiles
          } = await import('src/features/ttx/landing-sections/ttx-company-profiles');
          return {
            Component: TTXCompanyProfiles
          };
        },
        handle: {
          ...withoutFooter,
          name: 'ttx_company_profiles',
          isHeaderTransparent: true,
          hideIntercom: true
        }
      }, {
        path: 'generation/:exerciseType',
        async lazy() {
          const {
            newTTXGeneration
          } = await import('src/features/ttx/scenario-generation');
          return {
            Component: newTTXGeneration
          };
        },
        handle: {
          ...withoutFooter,
          name: 'ttx_generation'
        }
      }, {
        path: 'co-create/:scenarioId',
        loader: routeProtector().requireFeatureFlag('ttx-co-create'),
        async lazy() {
          const {
            StaticScenarioGeneration
          } = await import('src/features/ttx/static-scenario-generation/static-scenario-generation');
          return {
            Component: StaticScenarioGeneration
          };
        },
        handle: {
          ...fullScreen,
          hideIntercom: true,
          name: 'ttx_co_create'
        }
      }, {
        path: 'scenario/:scenarioId',
        loader: routeProtector().requireFeatureFlag('ttx-custom-exercise'),
        async lazy() {
          const {
            ScenarioReview
          } = await import('src/features/ttx/scenario-review');
          return {
            Component: ScenarioReview
          };
        },
        handle: {
          ...withoutFooter,
          name: 'ttx_scenario_review'
        }
      }, {
        path: 'scenario/:scenarioId/:stageId',
        loader: routeProtector().requireFeatureFlag('ttx-custom-exercise'),
        async lazy() {
          const {
            Customize
          } = await import('src/features/ttx/customize');
          return {
            Component: Customize
          };
        },
        handle: {
          name: 'ttx_customize',
          forcedTheme: ThemeColorMode.DARK
        }
      }, {
        path: 'join/:runId',
        async lazy() {
          const {
            TtxJoinRun
          } = await import('src/features/ttx');
          return {
            Component: TtxJoinRun
          };
        },
        handle: {
          name: 'ttx_join'
        }
      }, {
        path: 'narrative-conclusion/:runId',
        async lazy() {
          const {
            NarrativeConclusion
          } = await import('src/features/ttx/evaluation/evaluating');
          return {
            Component: NarrativeConclusion
          };
        },
        handle: {
          isHeaderSticky: false,
          name: 'ttx_narrative_conclusion',
          ...withoutFooter
        }
      }, {
        path: 'evaluation/:runId',
        async lazy() {
          const {
            Evaluation
          } = await import('src/features/ttx/evaluation/evaluation');
          return {
            Component: Evaluation
          };
        },
        handle: {
          isHeaderSticky: false,
          name: 'ttx_evaluation',
          ...withoutFooter
        }
      }]
    }, {
      // guard wrapper for routes that should only be accessible if there is an active run
      path: '',
      async lazy() {
        const {
          TtxRoom
        } = await import('src/features/ttx');
        return {
          Component: TtxRoom
        };
      },
      children: [{
        path: 'lobby/:runId',
        async lazy() {
          const {
            TtxLobby
          } = await import('src/features/ttx');
          return {
            Component: TtxLobby
          };
        },
        handle: {
          ...fullScreen,
          name: 'ttx_lobby',
          hideIntercom: true
        }
      }, {
        path: 'exercise/:runId',
        async lazy() {
          const {
            Exercise
          } = await import('src/features/ttx');
          return {
            Component: Exercise
          };
        },
        handle: {
          ...fullScreen,
          hideIntercom: true,
          name: 'ttx_exercise'
        }
      }]
    }]
  }, {
    path: 'manage',
    loader: routeProtector().requireOneOfRoles([UserRoles.TTX_ADMIN]),
    children: [{
      path: '',
      async lazy() {
        const {
          TTXAdmin
        } = await import('src/features/ttx/admin/ttx-admin');
        return {
          Component: TTXAdmin
        };
      },
      handle: {
        forcedTheme: ThemeColorMode.DARK
      }
    }],
    handle: {
      name: 'ttx_manage_assistants',
      forcedTheme: ThemeColorMode.DARK
    }
  }, {
    path: 'feedback',
    loader: routeProtector().requireLogin(),
    async lazy() {
      const {
        TtxFeedback
      } = await import('src/features/ttx/feedback');
      return {
        Component: TtxFeedback
      };
    },
    handle: {
      hideHeader: true,
      ...withoutFooter,
      hideIntercom: true,
      name: 'ttx_feedback',
      forcedTheme: ThemeColorMode.DARK
    }
  }]
}];